import React from "react";

const sketch = {
  gold: "#c9a962",
  teal: "#2563eb",
  tealLight: "#06b6d4",
  ink: "#334155",
  inkMuted: "#64748b",
};

function SketchPath({ d, delay = 0, stroke = sketch.gold, width = 2.2, className = "" }) {
  return (
    <path
      d={d}
      pathLength="100"
      className={`how-sketch-stroke ${className}`.trim()}
      stroke={stroke}
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      style={{ "--sketch-delay": `${delay}s` }}
    />
  );
}

function SketchSvg({ children, className, uid }) {
  const filterId = `how-sketch-wobble-${uid}`;
  return (
    <svg
      className={`how-graphic how-graphic--sketch ${className || ""}`.trim()}
      viewBox="0 0 200 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <defs>
        <filter id={filterId} x="-5%" y="-5%" width="110%" height="110%">
          <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="2" result="noise" />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="1.2" xChannelSelector="R" yChannelSelector="G" />
        </filter>
      </defs>
      <g className="how-sketch-layer" filter={`url(#${filterId})`}>
        {children}
      </g>
    </svg>
  );
}

const ProfileGraphic = () => (
  <SketchSvg className="how-graphic--profile" uid="profile">
    <SketchPath
      d="M28 78c2-8 14-14 28-15 18-1 36-2 54-1 20 1 38 4 52 10 6 3 8 9 6 15-2 6-8 10-16 10H40c-8 0-14-5-12-12z"
      delay={0}
      stroke={sketch.teal}
      width={2.4}
    />
    <SketchPath d="M52 63l10-12h24l8 12" delay={0.15} stroke={sketch.tealLight} />
    <SketchPath d="M48 78a10 10 0 1 1 0 .1M138 78a10 10 0 1 1 0 .1" delay={0.3} stroke={sketch.gold} />
    <SketchPath d="M42 70h108" delay={0.45} stroke={sketch.gold} width={1.5} className="how-sketch-stroke--scan" />
    <SketchPath d="M142 32h38a5 5 0 0 1 5 5v44a5 5 0 0 1-5 5h-38a5 5 0 0 1-5-5V37a5 5 0 0 1 5-5z" delay={0.55} stroke={sketch.gold} />
    <SketchPath d="M150 46h22M150 56h18M150 66h20" delay={0.7} stroke={sketch.inkMuted} width={1.6} />
    <circle className="how-sketch-fill" cx="150" cy="76" r="3" fill={sketch.teal} style={{ "--sketch-delay": "0.85s" }} />
  </SketchSvg>
);

const NetworkGraphic = () => (
  <SketchSvg className="how-graphic--network" uid="network">
    <SketchPath d="M100 60a22 22 0 1 1 0 .1" delay={0} stroke={sketch.gold} width={2} className="how-sketch-stroke--ring" />
    <SketchPath d="M100 60 L48 36" delay={0.2} stroke={sketch.tealLight} width={1.8} />
    <SketchPath d="M100 60 L152 36" delay={0.3} stroke={sketch.tealLight} width={1.8} />
    <SketchPath d="M100 60 L42 84" delay={0.4} stroke={sketch.tealLight} width={1.8} />
    <SketchPath d="M100 60 L158 84" delay={0.5} stroke={sketch.tealLight} width={1.8} />
    <SketchPath d="M100 60 L100 26" delay={0.6} stroke={sketch.tealLight} width={1.8} />
    <circle className="how-sketch-fill" cx="100" cy="60" r="12" fill="rgba(201,169,98,0.15)" stroke={sketch.gold} strokeWidth="2" style={{ "--sketch-delay": "0.1s" }} />
    {[
      [48, 36, 0.35],
      [152, 36, 0.45],
      [42, 84, 0.55],
      [158, 84, 0.65],
      [100, 26, 0.75],
    ].map(([cx, cy, delay]) => (
      <circle
        key={`${cx}-${cy}`}
        className="how-sketch-fill"
        cx={cx}
        cy={cy}
        r="7"
        fill="rgba(26,143,125,0.15)"
        stroke={sketch.teal}
        strokeWidth="1.8"
        style={{ "--sketch-delay": `${delay}s` }}
      />
    ))}
    <circle className="how-sketch-pulse" cx="72" cy="46" r="3" fill={sketch.gold} style={{ "--sketch-delay": "0.9s" }} />
    <circle className="how-sketch-pulse" cx="128" cy="46" r="3" fill={sketch.tealLight} style={{ "--sketch-delay": "1s" }} />
  </SketchSvg>
);

const OfferGraphic = () => (
  <SketchSvg className="how-graphic--offer" uid="offer">
    <SketchPath d="M46 98V74a4 4 0 0 1 4-4h14a4 4 0 0 1 4 4v24" delay={0} stroke={sketch.teal} />
    <SketchPath d="M78 98V58a4 4 0 0 1 4-4h14a4 4 0 0 1 4 4v40" delay={0.15} stroke={sketch.teal} />
    <SketchPath d="M110 98V42a4 4 0 0 1 4-4h14a4 4 0 0 1 4 4v56" delay={0.3} stroke={sketch.tealLight} width={2.4} />
    <SketchPath d="M50 82 L84 66 L116 50 L150 30" delay={0.45} stroke={sketch.gold} width={2.2} />
    <SketchPath
      d="M148 24l9 4v13c0 11-9 19-19 19h-5c-10 0-19-8-19-19V28l9-4 11 7 11-7z"
      delay={0.6}
      stroke={sketch.gold}
    />
    <SketchPath d="M142 38l5 5 9-9" delay={0.8} stroke={sketch.ink} width={2.5} />
    <path
      className="how-sketch-spark"
      d="M34 34l2 5 5 2-5 2-2 5-2-5-5-2 5-2z"
      fill={sketch.gold}
      style={{ "--sketch-delay": "0.95s" }}
    />
  </SketchSvg>
);

const PaymentGraphic = () => (
  <SketchSvg className="how-graphic--payment" uid="payment">
    <SketchPath d="M36 42h104a8 8 0 0 1 8 8v48a8 8 0 0 1-8 8H36a8 8 0 0 1-8-8V50a8 8 0 0 1 8-8z" delay={0} stroke={sketch.teal} width={2.3} />
    <SketchPath d="M28 56h120" delay={0.2} stroke={sketch.inkMuted} width={1.8} />
    <SketchPath d="M44 80h40a3 3 0 0 1 3 3v0a3 3 0 0 1-3 3H44a3 3 0 0 1-3-3v0a3 3 0 0 1 3-3z" delay={0.35} stroke={sketch.gold} />
    <circle className="how-sketch-fill" cx="148" cy="54" r="24" fill="rgba(201,169,98,0.08)" stroke={sketch.gold} strokeWidth="2" style={{ "--sketch-delay": "0.5s" }} />
    <SketchPath d="M136 54l8 8 16-16" delay={0.7} stroke={sketch.ink} width={2.6} />
    <circle className="how-sketch-fill" cx="54" cy="24" r="9" fill="rgba(201,169,98,0.12)" stroke={sketch.gold} strokeWidth="1.6" style={{ "--sketch-delay": "0.85s" }} />
    <text className="how-sketch-label" x="54" y="28" textAnchor="middle" fill={sketch.gold} fontSize="10" fontWeight="700" style={{ "--sketch-delay": "0.95s" }}>
      $
    </text>
    <circle className="how-sketch-fill" cx="78" cy="16" r="7" fill="rgba(26,143,125,0.12)" stroke={sketch.tealLight} strokeWidth="1.4" style={{ "--sketch-delay": "1.05s" }} />
    <text className="how-sketch-label" x="78" y="19" textAnchor="middle" fill={sketch.tealLight} fontSize="8" fontWeight="700" style={{ "--sketch-delay": "1.15s" }}>
      $
    </text>
  </SketchSvg>
);

const GRAPHICS = [ProfileGraphic, NetworkGraphic, OfferGraphic, PaymentGraphic];

export default function StepGraphic({ index, isActive, playKey }) {
  const Graphic = GRAPHICS[index] || ProfileGraphic;
  return (
    <div className={`how-it-works__graphic-wrap ${isActive ? "how-it-works__graphic-wrap--active" : ""}`}>
      <Graphic key={isActive ? `sketch-${index}-${playKey}` : `idle-${index}`} />
    </div>
  );
}
